import { useState, useEffect } from "react";
import { toast } from "sonner";
import { MagnifyingGlassIcon, TrashIcon, ArrowsClockwiseIcon, BooksIcon, ListNumbersIcon, ArticleIcon, XLogoIcon } from "@phosphor-icons/react";
import { fetchCustomStoryLibrary, deleteCustomStory } from "../lib/api";

const ago = (ts) => {
  if (!ts) return "";
  const s = Math.floor(Date.now() / 1000 - ts);
  if (s < 3600) return `${Math.max(1, Math.floor(s / 60))}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

export default function StoryLibrary({ onLoad, refreshKey = 0 }) {
  const [q, setQ] = useState("");
  const [stories, setStories] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async (term = "") => {
    setLoading(true);
    try {
      const d = await fetchCustomStoryLibrary(term);
      setStories(d.stories || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(""); }, [refreshKey]);
  useEffect(() => {
    const t = setTimeout(() => load(q.trim()), 300);
    return () => clearTimeout(t);
  }, [q]);

  const del = async (e, sid) => {
    e.stopPropagation();
    try {
      await deleteCustomStory(sid);
      setStories((s) => s.filter((x) => x.id !== sid));
      toast.success("Story deleted");
    } catch {
      toast.error("Delete failed");
    }
  };

  return (
    <div className="border border-white/10 bg-card p-5 sm:p-6" data-testid="story-library">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          <BooksIcon size={20} weight="fill" className="text-neon" />
          <h2 className="font-heading font-black text-lg tracking-tight">Story Library</h2>
          <span className="text-[11px] font-mono text-muted-foreground">{stories.length}</span>
        </div>
        <button data-testid="library-refresh-btn" onClick={() => load(q.trim())} className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-white/12 hover:border-neon/40 hover:text-neon transition-colors">
          <ArrowsClockwiseIcon size={13} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="flex items-center gap-2 bg-black/40 border border-white/12 focus-within:border-neon/50 px-3 mb-4">
        <MagnifyingGlassIcon size={15} className="text-muted-foreground shrink-0" />
        <input
          data-testid="library-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your stories — title, subject, ticker, CA…"
          className="w-full bg-transparent outline-none py-2.5 text-sm placeholder:text-muted-foreground/50"
        />
      </div>

      {stories.length === 0 ? (
        <p className="text-sm text-muted-foreground/70 py-6 text-center" data-testid="library-empty">
          {q ? "No stories match your search." : "No saved stories yet — write one above and it'll appear here."}
        </p>
      ) : (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1">
          {stories.map((s) => (
            <button
              key={s.id}
              data-testid={`library-item-${s.id}`}
              onClick={() => onLoad?.(s.id)}
              className="w-full text-left border border-white/10 hover:border-neon/40 bg-black/20 hover:bg-neon/[0.04] p-3 transition-colors group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    {s.mode === "thread" ? <ListNumbersIcon size={13} className="text-neon shrink-0" /> : <ArticleIcon size={13} className="text-neon shrink-0" />}
                    {s.symbol && <span className="text-[10px] font-mono px-1.5 py-0.5 border border-neon/30 text-neon shrink-0">${String(s.symbol).toUpperCase()}</span>}
                    {s.postedUrl && (
                      <a
                        href={s.postedUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        data-testid={`library-posted-${s.id}`}
                        title="Posted to X — view the tweet"
                        className="flex items-center gap-1 text-[9px] font-mono font-bold px-1.5 py-0.5 border border-sky-400/40 bg-sky-400/10 text-sky-300 shrink-0 hover:bg-sky-400/20 transition-colors"
                      >
                        <XLogoIcon size={10} weight="bold" /> POSTED
                      </a>
                    )}
                    <span className="text-sm font-semibold text-white truncate">{s.title}</span>
                  </div>
                  {s.subject && <p className="text-[11px] text-muted-foreground line-clamp-1">{s.subject}</p>}
                  <span className="text-[10px] font-mono text-muted-foreground/60">{ago(s.generatedAt)} · {s.angle}{s.hasChart ? " · 📊 chart" : ""}</span>
                </div>
                <span
                  role="button"
                  tabIndex={0}
                  data-testid={`library-delete-${s.id}`}
                  onClick={(e) => del(e, s.id)}
                  onKeyDown={(e) => { if (e.key === "Enter") del(e, s.id); }}
                  className="shrink-0 p-1.5 text-muted-foreground/50 hover:text-red-400 transition-colors opacity-0 group-hover:opacity-100 cursor-pointer"
                >
                  <TrashIcon size={14} />
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
